import { styled } from "@mui/material/styles";
import { Avatar, Box, Skeleton, Typography } from "@mui/material";
import { dashboardImg } from "../../assets/image";
import { capitalizeFirstLetter } from "../../utils";

interface UserCardProps {
	data: UserObject;
}

const UserCard = ({ data }: UserCardProps) => {
	return (
		<UserCardWrapper>
			<UserImageContainer>
				<img src={data.coverImage || dashboardImg} alt={capitalizeFirstLetter(data.name)} />
			</UserImageContainer>

			<UserContent>
				<StyledAvatar>
					<Avatar src={data.image} alt={capitalizeFirstLetter(data.name)} />
				</StyledAvatar>

				<Typography variant="h6">
					{capitalizeFirstLetter(data.name)}
				</Typography>

				<Typography variant="body2">
					{data.bio}
				</Typography>

				<Typography variant="body2" component="span">
					<span>Students:</span> {data.totalStudents}+
				</Typography>
			</UserContent>
		</UserCardWrapper>
	)
}

const UserCardSkeleton = () => {
	return (
		<UserCardWrapper>
			<UserImageContainer>
				<Skeleton variant="rectangular" width="100%" height="100%" />
			</UserImageContainer>

			<UserContent>
				<StyledAvatar>
					<Skeleton variant="rounded" width="100%" height="100%" />
				</StyledAvatar>

				<Skeleton variant="text" height={35} width="50%" />
				<Skeleton variant="text" height={25} />
				<Skeleton variant="text" height={25} width={50} />
			</UserContent>
		</UserCardWrapper>
	)
}

const UserCardWrapper = styled(Box)(({ theme }) => ({
	maxWidth: 350,
	margin: "0 auto",
	position: "relative",
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,
	transition: "transform 0.2s, box-shadow 0.2s",

	height: "100%",
	overflow: "hidden",
	cursor: "pointer",


	h5: {
		zIndex: 2,
		top: 0, left: 0,
		position: "absolute",
		display: "inline-block",
		padding: theme.spacing(0.5, 2),
		borderRadius: `0 0 ${theme.spacing(1)} 0`,
		backgroundColor: `${theme.palette.common.thirdColor}80`,

		fontWeight: 500,
		textTransform: "capitalize",
		color: theme.palette.common.secondColor,
	},

	"&:hover": {
		transform: "translateY(-4px)",
	},
}))

const UserImageContainer = styled(Box)({
	width: "100%",
	position: "relative",
	paddingTop: "56.25%", // 16:9 Aspect Ratio

	"& img, .MuiSkeleton-root": {
		top: 0, left: 0,
		position: "absolute",
		width: "100%",
		height: "100%",
		objectFit: "cover",
	}
})

const UserContent = styled(Box)(({ theme }) => ({
	position: "relative",

	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(0.7),
	padding: theme.spacing(1.5),
	paddingTop: 60,

	h6: {
		fontWeight: 600,
		WebkitLineClamp: 2,
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		lineHeight: 1.2,
		overflow: "hidden",
	},

	p: {
		flex: 1,
		WebkitLineClamp: 1,
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		color: `${theme.palette.common.firstColor}c0`,
		overflow: "hidden",
	},

	span: {
		fontWeight: 600,
		marginTop: "auto",

		span: {
			fontWegith: 400,
			color: `${theme.palette.common.firstColor}c0`,
		}
	},

	[theme.breakpoints.down('md')]: {
		paddingTop: 50,
	}
}))

const StyledAvatar = styled(Box)(({ theme }) => ({
	top: -50, left: 20,
	position: "absolute",
	width: 100, height: 100,

	borderRadius: "50%",
	border: `4px solid ${theme.palette.common.white}`,
	boxShadow: `0px 1px 5px 2px ${theme.palette.common.firstBorder}`,

	".MuiAvatar-root": {
		fontWeight: 500,
		fontSize: theme.typography.h2.fontSize,
	},

	".MuiSkeleton-root, .MuiAvatar-root, .MuiAvatar-root img": {
		width: "100%",
		height: "100%",
		borderRadius: "50%",
	},

	[theme.breakpoints.down('md')]: {
		width: 80, height: 80,
		top: -40, left: 20,
	}
}))

export { UserCard, UserCardSkeleton };